import { useEffect, useMemo, useState } from 'react'
import { getClikArtResults } from '@/lib/boundaries/clikart-boundary'
import { useClikArtCategoriesQuery } from '@/api/hooks/use-clikart-categories'
import { useClikArtKeywordsQuery } from '@/api/hooks/use-clikart-keywords'
import { useClikArtSearchQuery } from '@/api/hooks/use-clikart-search'
import type { ClikArtCmsKeyword, ClikArtItem } from '@/api/types/clikart'
import { filterClikArtByCategory } from '@/lib/clikart/clikart-category-filter'
import {
  type ClikArtPlacedFilter,
  filterClikArtByPlaced,
} from '@/lib/clikart/clikart-placed-filter'
import { applyClikArtPlacementOverrides } from '@/lib/clikart/clikart-placement-session'
import { useClikArtPlacementOverrides } from '@/hooks/use-clikart-placement-overrides'
import { resolveClikArtKeywordIds } from '@/lib/clikart/resolve-clikart-keyword-ids'
import { type ClikArtSortValue, sortClikArt } from '@/lib/clikart/clikart-sort'

// Stable empty-array fallbacks — `data ?? []` would mint a new reference on every render while a
// query has no data yet, which would re-trigger the effects/memos that depend on it every render.
const EMPTY_CLIKART_ITEMS: ClikArtItem[] = []
const EMPTY_CLIKART_KEYWORDS: ClikArtCmsKeyword[] = []

type ClikArtResultsInput = {
  category: string
  searchQuery: string
  selectedKeywords: string[]
  placedFilter: ClikArtPlacedFilter
  sortValue: ClikArtSortValue
}

/**
 * The ClikArt gallery's results: server search (or the categories endpoint when nothing is
 * searched), narrowed client-side by Category and Placed, then run through the JS/WASM results
 * boundary and sorted.
 */
export function useClikArtResults({
  category,
  searchQuery,
  selectedKeywords,
  placedFilter,
  sortValue,
}: ClikArtResultsInput) {
  // `SearchKeywordComboBox` (and `selectedKeywords` above) work in terms of keyword NAMES, like
  // every other combobox in this panel — resolved to ids only where the server search needs them.
  const keywordsQuery = useClikArtKeywordsQuery()
  const allKeywords = keywordsQuery.data ?? EMPTY_CLIKART_KEYWORDS
  const keywordNames = useMemo(() => allKeywords.map((k) => k.cmsKeywordName), [allKeywords])
  const keywordIds = useMemo(
    () => resolveClikArtKeywordIds(selectedKeywords, allKeywords),
    [selectedKeywords, allKeywords]
  )

  // Filename search and keyword ids are real server-side params (`/clikart/search` — see
  // `clikart.service.ts`); Category and Placed have no server equivalent and stay client-side
  // filters over whatever the active query already returned (AC-7/AC-8 WMPCM-10942, AC-4
  // WMPCM-10948).
  const searchResultQuery = useClikArtSearchQuery(searchQuery, keywordIds)

  // Confirmed live (2026-09-29): /clikart/search with neither param set returns `payload: []` —
  // it has no "give me everything" mode. /clikart/categories is the only endpoint that can power
  // the gallery's default view (nothing typed/selected) — confirmed with backend — so that's what
  // drives the panel whenever there's no search criteria; /clikart/search takes over once there is.
  const categoriesQuery = useClikArtCategoriesQuery()
  const hasSearchCriteria = searchQuery.trim() !== '' || keywordIds.length > 0
  const activeQuery = hasSearchCriteria ? searchResultQuery : categoriesQuery
  const serverData = activeQuery.data ?? EMPTY_CLIKART_ITEMS
  const placementOverrides = useClikArtPlacementOverrides()

  // The server's `placedFlag` is only as fresh as its last book scan — overlay what this session
  // has placed or removed on any spread so the badge and the Placed filter follow the book live.
  const searchData = useMemo(
    () => applyClikArtPlacementOverrides(serverData, placementOverrides),
    [serverData, placementOverrides]
  )

  // Synchronous half of the pipeline (category/Placed have no server param — see
  // `clikart.service.ts` — and stay client-side filters over whatever the server returned).
  const filtered = useMemo(
    () => filterClikArtByPlaced(filterClikArtByCategory(searchData, category), placedFilter),
    [searchData, category, placedFilter]
  )

  // Seeded from `filtered` (not `[]`) so the gallery reflects real data on the very first render,
  // not just after the async boundary call below resolves — matching the old items-are-already-
  // synchronously-available behavior this panel's tests are written against.
  const [results, setResults] = useState<ClikArtItem[]>(filtered)

  useEffect(() => {
    let cancelled = false

    // getClikArtResults' JS/WASM boundary is keyed on `name`, not `displayName` (see
    // clikart-boundary.ts) — round-tripped as an alias so every other field survives untouched.
    // Re-applying the immediate (non-debounced) searchQuery here — on top of the server's own
    // debounced displayName filter already reflected in searchData — keeps the grid narrowing
    // responsively while a keystroke's server round-trip is still in flight.
    const boundaryItems = filtered.map((item) => ({ ...item, name: item.displayName }))

    void getClikArtResults(searchQuery, boundaryItems).then((computed) => {
      if (!cancelled) setResults(sortClikArt(computed, sortValue) as unknown as ClikArtItem[])
    })

    return () => {
      cancelled = true
    }
  }, [filtered, searchQuery, sortValue])

  return {
    keywordNames,
    keywordIds,
    isLoading: activeQuery.isLoading,
    isError: activeQuery.isError,
    results,
  }
}
